import React, { useCallback, useEffect, useState } from 'react';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import Button from 'flow/components/ui-components/Button';
import Checkbox from 'flow/components/ui-components/Checkbox';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import { AdvancedSearchFilters } from 'flow/types/Tickets';
import { selectAllTagsAvailableForTicketTagging } from 'flow/selectors/TicketsSelectors';
import { fetchAllAvailableTags } from 'flow/actions/tickets-action';
import AssigneePicker from './AssigneePicker';
import SinceDatePicker from './SinceDatePicker';
import TagPicker from './TagPicker';
import { hasAnyAdvancedFilterActive } from './filter-utils';

const EMPTY_FILTERS: AdvancedSearchFilters = { marked: false, since: '' };

const STATE_OPTIONS = [
    { key: '', label: 'tickets.filter.all' },
    { key: 'open', label: 'ticket.status.open_plural' },
    { key: 'closed', label: 'ticket.status.closed_plural' },
];

interface Props {
    /** Currently applied filter set. The popup edits a local draft
     *  seeded from this value; the parent is notified when the user
     *  clicks Apply or Reset. */
    filters: AdvancedSearchFilters;
    onApply: (next: AdvancedSearchFilters) => void;
    onClose?: () => void;
}

/**
 * Filter popup body. Selections are buffered in a local draft and
 * committed to the parent (triggering a backend request) on Apply
 * (which also closes the popup) or Reset (which stays open with a
 * cleared draft so the user can build a new filter).
 */
const AdvancedSearchPanelContent = ({ filters, onApply, onClose }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [draft, setDraft] = useState<AdvancedSearchFilters>(filters);

    // Optimistic default: until the catalog request settles, assume
    // tags exist so the section stays active. Once we have a response,
    // gate dimming on the actual content.
    const [tagsFetched, setTagsFetched] = useState(false);
    useEffect(() => {
        dispatch(fetchAllAvailableTags()).finally(() => setTagsFetched(true));
    }, [dispatch]);

    // Partial-update helper — only one filter field changes per
    // event; the local draft always carries a complete filter set.
    const updateFilter = useCallback(
        <K extends keyof AdvancedSearchFilters>(
            key: K,
            value: AdvancedSearchFilters[K],
        ) => {
            setDraft((prev) => ({ ...prev, [key]: value }));
        },
        [],
    );

    const currentStateOption =
        STATE_OPTIONS.find((o) => o.key === (draft.state ?? '')) ??
        STATE_OPTIONS[0];

    const hasAnyDraftFilter = hasAnyAdvancedFilterActive(draft);
    const hasAnyAppliedFilter = hasAnyAdvancedFilterActive(filters);

    const handleApply = useCallback(() => {
        onApply(draft);
        onClose?.();
    }, [draft, onApply, onClose]);

    // Reset clears both the draft and the applied filters, but keeps
    // the popup open so the user can immediately build a new filter
    // from a clean slate.
    const handleReset = useCallback(() => {
        setDraft(EMPTY_FILTERS);
        onApply(EMPTY_FILTERS);
    }, [onApply]);

    const allTags = useAppSelector(selectAllTagsAvailableForTicketTagging);
    const noTagsAvailable = tagsFetched && allTags.size === 0;

    return (
        <div className="flex flex-col gap-y-3">
            <div>
                <div className="mb-1.5 text-xs font-bold text-gray-800">
                    {i18next.t('tickets.filter.assignee')}
                </div>
                <AssigneePicker
                    value={draft.assignee}
                    onChange={(v) => updateFilter('assignee', v)}
                />
            </div>
            <div>
                <div className="mb-1.5 text-xs font-bold text-gray-800">
                    {i18next.t('tickets.filter.status')}
                </div>
                {/* Inline Menu — matches the panel's trigger width
                 *  exactly (the dropdown panel reuses the trigger's
                 *  `w-full`). */}
                <Menu as="div" className="relative text-left">
                    {/* Absolute chevron at end-2 / size-6 matches
                     *  the Combobox pickers' chevron position so the
                     *  right edge of each field reads as one column. */}
                    <MenuButton className="hover:darken-10 relative flex w-full items-center rounded-sm bg-gray-200 py-2 ps-3 pe-9 text-sm text-gray-800 dark:bg-gray-100">
                        <span>{i18next.t(currentStateOption.label)}</span>
                        <span className="absolute end-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center text-gray-600">
                            <Icon iconKey="arrow_down" size="1.5rem" />
                        </span>
                    </MenuButton>
                    <MenuItems className="scrollbar shadow-base scrollbar-gutter-stable absolute end-0 z-20 mt-2 max-h-72 w-full origin-top-right rounded-md bg-white py-2 ps-2 outline-gray-100 focus:outline dark:bg-gray-300">
                        {STATE_OPTIONS.map((option) => (
                            <MenuItem
                                key={`mi-${option.key}`}
                                onClick={() =>
                                    updateFilter(
                                        'state',
                                        option.key === ''
                                            ? undefined
                                            : (option.key as 'open' | 'closed'),
                                    )
                                }
                                dataTestId={`dropdown-option-${option.key}`}
                            >
                                <span>{i18next.t(option.label)}</span>
                            </MenuItem>
                        ))}
                    </MenuItems>
                </Menu>
            </div>
            <div className={twMerge(noTagsAvailable && 'opacity-50')}>
                <div className="mb-1.5 text-xs font-bold text-gray-800">
                    {i18next.t('tickets.filter.tags')}
                </div>
                <TagPicker
                    value={draft.tagKeysOr ?? []}
                    onChange={(next) =>
                        updateFilter(
                            'tagKeysOr',
                            next.length > 0 ? next : undefined,
                        )
                    }
                    disabled={noTagsAvailable}
                />
            </div>
            <div>
                <div className="mb-1.5 text-xs font-bold text-gray-800">
                    {i18next.t('tickets.filter.since')}
                </div>
                <SinceDatePicker
                    value={draft.since}
                    onChange={(v) => updateFilter('since', v)}
                />
            </div>
            <Checkbox
                label={
                    <span className="flex items-center gap-1.5">
                        {i18next.t('tickets.filter.marked')}
                        <span className="size-2 shrink-0 rounded-full bg-red-500" />
                    </span>
                }
                checked={draft.marked}
                onChange={(_e, checked) => updateFilter('marked', checked)}
            />
            <div className="flex gap-x-2">
                <Button
                    color="gray"
                    disabled={!hasAnyDraftFilter && !hasAnyAppliedFilter}
                    onClick={handleReset}
                    className="h-9 flex-1 rounded-md text-gray-700 disabled:bg-gray-100 disabled:text-gray-400"
                    hideTextOnOnePanel={true}
                >
                    {i18next.t('tickets.filter.reset')}
                </Button>
                <Button
                    color="primary"
                    onClick={handleApply}
                    className="h-9 flex-1 rounded-md"
                    hideTextOnOnePanel={true}
                >
                    {i18next.t('tickets.filter.apply')}
                </Button>
            </div>
        </div>
    );
};

export default AdvancedSearchPanelContent;
